import { useQuery } from '@tanstack/react-query';
import { api } from './api';
import type { LegalConfig, SocialPlatform } from './types';

/**
 * Social / external profiles the site knows how to render + label. Order here is the render order
 * (footer, contact, BO editor). Value is a plain URL string; the `placeholder` seeds the BO input.
 * These are surfaced publicly AND emitted as Organization.sameAs — the strongest entity signal an
 * LLM uses to disambiguate "SieveGuard". Only add a URL once the profile is real.
 */
export const SOCIAL_PLATFORMS: ReadonlyArray<{ key: SocialPlatform; label: string; placeholder: string }> = [
  { key: 'linkedin', label: 'LinkedIn', placeholder: 'https://www.linkedin.com/company/…' },
  { key: 'x', label: 'X', placeholder: 'https://x.com/…' },
  { key: 'github', label: 'GitHub', placeholder: 'https://github.com/…' },
  { key: 'crunchbase', label: 'Crunchbase', placeholder: 'https://www.crunchbase.com/organization/…' },
  { key: 'producthunt', label: 'Product Hunt', placeholder: 'https://www.producthunt.com/products/…' },
  { key: 'bluesky', label: 'Bluesky', placeholder: 'https://bsky.app/profile/…' },
  { key: 'youtube', label: 'YouTube', placeholder: 'https://www.youtube.com/@…' },
  { key: 'reddit', label: 'Reddit', placeholder: 'https://www.reddit.com/r/…' },
];

/**
 * Shipped default until a super-admin configures the setting. Only the real, existing profile —
 * everything else is added in Admin → Settings → Public site as the accounts get created.
 */
export const DEFAULT_SOCIAL_LINKS: Partial<Record<SocialPlatform, string>> = {
  linkedin: 'https://www.linkedin.com/company/sieveguard/',
};

/**
 * Ordered, render-ready list from a stored map. `undefined` means the operator never configured it
 * → fall back to the shipped default; an explicit (even empty) map is the operator's choice and is
 * honoured as-is, so clearing a profile actually removes it. Re-validates https defensively (the
 * API already sanitises on write).
 */
export function socialLinkList(
  map: Partial<Record<SocialPlatform, string>> | undefined,
): Array<{ key: SocialPlatform; label: string; url: string }> {
  const src = map ?? DEFAULT_SOCIAL_LINKS;
  return SOCIAL_PLATFORMS.flatMap(({ key, label }) => {
    const url = src[key];
    return url && /^https:\/\//i.test(url) ? [{ key, label, url }] : [];
  });
}

/**
 * Hook form for schema emission (Organization.sameAs). Reads the same `['legal-config']` query the
 * footer/contact use — seeded from the SSG snapshot, so the prerender bakes the real URLs.
 */
export function useSocialLinks(): Array<{ key: SocialPlatform; label: string; url: string }> {
  const { data } = useQuery({
    queryKey: ['legal-config'],
    queryFn: () => api<LegalConfig>('/legal/config', { auth: false }),
    staleTime: 60 * 60 * 1000,
  });
  return socialLinkList(data?.socialLinks);
}
